import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState } from '../components/common/EmptyState';
import { ProjectGuard } from '../components/common/ProjectGuard';
import { useProjectStore } from '../stores/project.store';
import { useProject } from '../api/queries/projects';

export function BoardListRoute() {
  const { activeProjectId } = useProjectStore();

  return (
    <ProjectGuard>
      {activeProjectId && <BoardListContent projectId={activeProjectId} />}
    </ProjectGuard>
  );
}

function BoardListContent({ projectId }: { projectId: string }) {
  const { data: project, isLoading, isError } = useProject(projectId);
  const navigate = useNavigate();

  const boardId = project?.boardId;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Creativity Board{project ? ` \u2014 ${project.name}` : ''}
        </Typography>
      </Stack>

      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">Failed to load project.</Alert>}

      {boardId && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <DashboardIcon color="primary" sx={{ fontSize: 40 }} />
              <Stack sx={{ flex: 1 }}>
                <Typography variant="h6">Project Board</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pin notes, reference images, and ideas to a freeform board.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={() =>
                  navigate({
                    to: '/boards/$boardId',
                    params: { boardId },
                  })
                }
              >
                Open Board
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!isLoading && !boardId && (
        <EmptyState
          icon={<DashboardIcon sx={{ fontSize: 64 }} />}
          title="No board available"
          description="A creativity board will be created when the project is scaffolded. Go to the project page to get started."
        />
      )}
    </Paper>
  );
}
